import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Pencil, Trash2, ArrowLeft, Map, GripVertical,
  ChevronDown, ChevronRight, Save, X,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

/**
 * AdminJourney — admin CRUD for The Journey.
 *
 *  • Left column: phases list with inline rename, add, delete.
 *  • Right column: selected phase's steps with add/edit/delete + content config.
 *  • Reorder via drag handle (basic up/down buttons for now — robust DnD later).
 */

const CONTENT_TYPES = [
  { id: "video", label: "Video", help: "YouTube / Vimeo URL" },
  { id: "text", label: "Text", help: "Lesson body (markdown supported)" },
  { id: "doc", label: "Document", help: "PDF / DOCX hosted URL" },
  { id: "link", label: "External Link", help: "Any reference URL" },
];

export default function AdminJourney() {
  const [phases, setPhases] = useState([]);
  const [activePhaseId, setActivePhaseId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [phaseForm, setPhaseForm] = useState(null); // {id?, title, description}
  const [stepForm, setStepForm] = useState(null); // {id?, phase_id, ...}
  const [confirm, setConfirm] = useState(null); // {title, desc, onConfirm}

  const load = async () => {
    const { data } = await api.get("/journey");
    setPhases(data);
    setActivePhaseId((curr) => curr ?? (data[0]?.id || null));
  };

  useEffect(() => {
    setLoading(true);
    load().catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const activePhase = useMemo(
    () => phases.find((p) => p.id === activePhaseId),
    [phases, activePhaseId],
  );

  // ----- Phase actions -----
  const openCreatePhase = () => setPhaseForm({ title: "", description: "" });
  const openEditPhase = (p) => setPhaseForm({ id: p.id, title: p.title, description: p.description || "" });

  const savePhase = async () => {
    if (!phaseForm.title?.trim()) return toast.error("Title is required");
    try {
      if (phaseForm.id) {
        await api.put(`/admin/journey/phases/${phaseForm.id}`, {
          title: phaseForm.title.trim(),
          description: phaseForm.description?.trim() || null,
          order: phases.find((p) => p.id === phaseForm.id)?.order ?? 0,
        });
        toast.success("Phase updated");
      } else {
        await api.post("/admin/journey/phases", {
          title: phaseForm.title.trim(),
          description: phaseForm.description?.trim() || null,
        });
        toast.success("Phase created");
      }
      setPhaseForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deletePhase = (p) => {
    setConfirm({
      title: `Delete "${p.title}"?`,
      desc: `This removes the phase, ${p.steps?.length || 0} step(s), and all student progress on them. Cannot be undone.`,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/journey/phases/${p.id}`);
          toast.success("Phase deleted");
          setConfirm(null);
          if (activePhaseId === p.id) setActivePhaseId(null);
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const movePhase = async (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= phases.length) return;
    const reordered = [...phases];
    const [m] = reordered.splice(idx, 1);
    reordered.splice(next, 0, m);
    setPhases(reordered);
    try {
      await api.post("/admin/journey/phases/reorder", { ids: reordered.map((p) => p.id) });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  // ----- Step actions -----
  const openCreateStep = () => {
    if (!activePhase) return toast.error("Select a phase first");
    setStepForm({
      phase_id: activePhase.id,
      title: "",
      content_type: "video",
      video_url: "",
      content_md: "",
      external_url: "",
      minutes: "",
      emoji: "",
    });
  };

  const openEditStep = (s) => setStepForm({
    id: s.id,
    phase_id: s.phase_id,
    title: s.title || "",
    content_type: s.content_type || "video",
    video_url: s.video_url || "",
    content_md: s.content_md || "",
    external_url: s.external_url || "",
    minutes: s.minutes ?? "",
    emoji: s.emoji || "",
    order: s.order ?? 0,
  });

  const saveStep = async () => {
    if (!stepForm.title?.trim()) return toast.error("Title is required");
    const body = {
      title: stepForm.title.trim(),
      content_type: stepForm.content_type,
      video_url: stepForm.video_url?.trim() || null,
      content_md: stepForm.content_md || null,
      external_url: stepForm.external_url?.trim() || null,
      minutes: stepForm.minutes === "" ? null : Number(stepForm.minutes),
      emoji: stepForm.emoji?.trim() || null,
      order: stepForm.order ?? 0,
    };
    try {
      if (stepForm.id) {
        await api.put(`/admin/journey/steps/${stepForm.id}`, body);
        toast.success("Step updated");
      } else {
        await api.post(`/admin/journey/phases/${stepForm.phase_id}/steps`, body);
        toast.success("Step created");
      }
      setStepForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteStep = (s) => {
    setConfirm({
      title: `Delete "${s.title}"?`,
      desc: "This removes the step and any student progress on it. Cannot be undone.",
      onConfirm: async () => {
        try {
          await api.delete(`/admin/journey/steps/${s.id}`);
          toast.success("Step deleted");
          setConfirm(null);
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const moveStep = async (idx, dir) => {
    if (!activePhase) return;
    const steps = [...(activePhase.steps || [])];
    const next = idx + dir;
    if (next < 0 || next >= steps.length) return;
    const [m] = steps.splice(idx, 1);
    steps.splice(next, 0, m);
    // optimistic
    setPhases((prev) => prev.map((p) => p.id === activePhase.id ? { ...p, steps } : p));
    try {
      await api.post(`/admin/journey/phases/${activePhase.id}/steps/reorder`, { ids: steps.map((s) => s.id) });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="admin-journey-page">
        <div className="flex items-center justify-center py-20"><span className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-journey-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-7">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: "var(--lime-glow)", color: "var(--lime)" }}
          >
            <Map size={22} strokeWidth={1.6} />
          </div>
          <div>
            <div className="section-tag mb-2"><span className="dot" /> Admin</div>
            <h1 className="h-display text-3xl sm:text-4xl">Manage The Journey</h1>
            <p className="text-sm mt-2 max-w-lg" style={{ color: "var(--text-muted)" }}>
              Create the curriculum. Phases group the lessons; steps hold the videos, docs, and text.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/journey"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition hover:bg-white/5"
            style={{ color: "var(--text-muted)", border: "1px solid var(--border)" }}
            data-testid="admin-journey-preview-link"
          >
            <ArrowLeft size={14} /> Student View
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        {/* Phases column */}
        <div className="surface-card p-4">
          <div className="flex items-center justify-between mb-4 px-1">
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
              Phases ({phases.length})
            </span>
            <button
              onClick={openCreatePhase}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5"
              style={{ background: "var(--lime)", color: "#0c0c0c" }}
              data-testid="admin-journey-add-phase"
            >
              <Plus size={12} /> Phase
            </button>
          </div>
          <div className="flex flex-col gap-2 max-h-[700px] overflow-y-auto pr-1">
            {phases.map((p, idx) => {
              const isActive = p.id === activePhaseId;
              return (
                <div
                  key={p.id}
                  className="rounded-2xl p-3 group transition"
                  style={{
                    background: isActive ? "var(--lime-glow)" : "var(--surface2)",
                    border: isActive ? "1px solid var(--lime)" : "1px solid var(--border)",
                  }}
                  data-testid={`admin-phase-row-${p.id}`}
                >
                  <button onClick={() => setActivePhaseId(p.id)} className="w-full text-left">
                    <div className="font-mono text-[10px] uppercase tracking-wider mb-0.5" style={{ color: isActive ? "var(--lime)" : "var(--text-dim)" }}>
                      Phase {idx + 1}
                    </div>
                    <div className="text-sm font-semibold">{p.title}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-dim)" }}>
                      {p.steps?.length || 0} step{(p.steps?.length || 0) === 1 ? "" : "s"}
                    </div>
                  </button>
                  <div className="flex items-center gap-1 mt-2">
                    <button onClick={() => movePhase(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-white/5 disabled:opacity-40" title="Move up">
                      <ChevronDown size={12} className="rotate-180" />
                    </button>
                    <button onClick={() => movePhase(idx, 1)} disabled={idx === phases.length - 1} className="p-1 rounded hover:bg-white/5 disabled:opacity-40" title="Move down">
                      <ChevronDown size={12} />
                    </button>
                    <div className="flex-1" />
                    <button onClick={() => openEditPhase(p)} className="p-1.5 rounded hover:bg-white/5" title="Edit" data-testid={`admin-phase-edit-${p.id}`}>
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => deletePhase(p)} className="p-1.5 rounded hover:bg-white/5" style={{ color: "#f87171" }} title="Delete" data-testid={`admin-phase-delete-${p.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Steps column */}
        <div>
          {activePhase ? (
            <div>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div>
                  <h2 className="h-display text-2xl mb-1">{activePhase.title}</h2>
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                    {activePhase.description || "Add a short description in Edit Phase to give students context."}
                  </p>
                </div>
                <button
                  onClick={openCreateStep}
                  className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
                  style={{ background: "var(--lime)", color: "#0c0c0c" }}
                  data-testid="admin-journey-add-step"
                >
                  <Plus size={14} /> Add Step
                </button>
              </div>

              {(activePhase.steps || []).length === 0 ? (
                <div className="surface-card p-10 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
                    <Plus size={18} />
                  </div>
                  <h3 className="font-semibold mb-1">No steps yet</h3>
                  <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
                    Add the first lesson to start populating this phase.
                  </p>
                  <button
                    onClick={openCreateStep}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background: "var(--lime)", color: "#0c0c0c" }}
                  >
                    Add Step
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {activePhase.steps.map((s, i) => (
                    <AdminStepRow
                      key={s.id}
                      step={s}
                      index={i}
                      total={activePhase.steps.length}
                      onEdit={() => openEditStep(s)}
                      onDelete={() => deleteStep(s)}
                      onMove={(dir) => moveStep(i, dir)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="surface-card p-10 text-center">
              <h3 className="font-semibold mb-1">Select a phase</h3>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                Pick a phase from the left to manage its steps.
              </p>
            </div>
          )}
        </div>
      </div>

      {phaseForm && (
        <PhaseFormModal form={phaseForm} setForm={setPhaseForm} onSave={savePhase} onClose={() => setPhaseForm(null)} />
      )}
      {stepForm && (
        <StepFormModal form={stepForm} setForm={setStepForm} onSave={saveStep} onClose={() => setStepForm(null)} />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        description={confirm?.desc}
        destructive
        confirmLabel="Delete"
        onCancel={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
      />
    </div>
  );
}

function AdminStepRow({ step, index, total, onEdit, onDelete, onMove }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="p-4 rounded-2xl"
      style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
      data-testid={`admin-step-row-${step.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs shrink-0"
          style={{ background: "var(--surface2)", color: "var(--text-muted)" }}
        >
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <h4 className="text-sm font-semibold">{step.title}{step.emoji ? ` ${step.emoji}` : ""}</h4>
            <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(96,165,250,0.12)", color: "#60a5fa" }}>
              {step.content_type}
            </span>
            {step.minutes ? (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
                {step.minutes} min
              </span>
            ) : null}
          </div>
          {open && (
            <div className="mt-2 text-xs space-y-1" style={{ color: "var(--text-muted)" }}>
              {step.video_url && <div>Video: <a href={step.video_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--lime)" }}>{step.video_url}</a></div>}
              {step.external_url && <div>URL: <a href={step.external_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--lime)" }}>{step.external_url}</a></div>}
              {step.content_md && <div className="whitespace-pre-wrap pt-1">{step.content_md.slice(0, 200)}{step.content_md.length > 200 ? "…" : ""}</div>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-40" title="Move up">
            <ChevronDown size={12} className="rotate-180" />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-40" title="Move down">
            <ChevronDown size={12} />
          </button>
          <button onClick={() => setOpen((v) => !v)} className="p-1.5 rounded hover:bg-white/5" title="Details">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <button onClick={onEdit} className="p-1.5 rounded hover:bg-white/5" data-testid={`admin-step-edit-${step.id}`}>
            <Pencil size={13} />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-white/5" style={{ color: "#f87171" }} data-testid={`admin-step-delete-${step.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, onClose, children, footer, testId }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className="surface-card w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/5" style={{ color: "var(--text-muted)" }}>
            <X size={18} />
          </button>
        </div>
        <div className="px-6 pb-2">{children}</div>
        <div className="px-6 pb-6 pt-3 flex items-center justify-end gap-2 border-t" style={{ borderColor: "var(--border)" }}>
          {footer}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-mono uppercase tracking-wider mb-1.5" style={{ color: "var(--text-muted)" }}>
        {label}
      </label>
      {children}
      {hint && <div className="text-[11px] mt-1" style={{ color: "var(--text-dim)" }}>{hint}</div>}
    </div>
  );
}

function PhaseFormModal({ form, setForm, onSave, onClose }) {
  return (
    <ModalShell
      testId="admin-phase-modal"
      title={form.id ? "Edit Phase" : "New Phase"}
      onClose={onClose}
      footer={(
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm" style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}>Cancel</button>
          <button onClick={onSave} className="px-5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: "var(--lime)", color: "#0c0c0c" }} data-testid="admin-phase-save">
            <Save size={14} /> Save
          </button>
        </>
      )}
    >
      <Field label="Title">
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="input-base"
          placeholder="e.g. Lead Generation"
          autoFocus
          data-testid="admin-phase-title-input"
        />
      </Field>
      <Field label="Description" hint="Shown beneath the phase title on the student page.">
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className="input-base"
          placeholder="What students will learn in this phase…"
          data-testid="admin-phase-description-input"
        />
      </Field>
    </ModalShell>
  );
}

function StepFormModal({ form, setForm, onSave, onClose }) {
  const ct = form.content_type;
  return (
    <ModalShell
      testId="admin-step-modal"
      title={form.id ? "Edit Step" : "New Step"}
      onClose={onClose}
      footer={(
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm" style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}>Cancel</button>
          <button onClick={onSave} className="px-5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: "var(--lime)", color: "#0c0c0c" }} data-testid="admin-step-save">
            <Save size={14} /> Save
          </button>
        </>
      )}
    >
      <Field label="Title">
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="input-base"
          placeholder="e.g. Calling Script Walkthrough"
          autoFocus
          data-testid="admin-step-title-input"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Emoji" hint="Optional accent">
          <input
            type="text"
            value={form.emoji}
            onChange={(e) => setForm({ ...form, emoji: e.target.value })}
            className="input-base"
            placeholder="🎯"
          />
        </Field>
        <Field label="Minutes" hint="Estimated duration">
          <input
            type="number"
            min="0"
            value={form.minutes}
            onChange={(e) => setForm({ ...form, minutes: e.target.value })}
            className="input-base"
            placeholder="10"
          />
        </Field>
      </div>

      <Field label="Content Type">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CONTENT_TYPES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setForm({ ...form, content_type: c.id })}
              className="p-2.5 rounded-xl text-xs font-semibold transition"
              style={{
                background: ct === c.id ? "var(--lime-glow)" : "var(--surface2)",
                border: ct === c.id ? "1px solid var(--lime)" : "1px solid var(--border)",
                color: ct === c.id ? "var(--lime)" : "var(--text)",
              }}
              data-testid={`admin-step-type-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </Field>

      {ct === "video" && (
        <Field label="Video URL" hint="YouTube or Vimeo link — we'll auto-embed it.">
          <input
            type="url"
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            className="input-base"
            placeholder="https://www.youtube.com/watch?v=…"
            data-testid="admin-step-video-input"
          />
        </Field>
      )}

      {(ct === "doc" || ct === "link") && (
        <Field label={ct === "doc" ? "Document URL" : "External URL"} hint="Public URL students will open in a new tab.">
          <input
            type="url"
            value={form.external_url}
            onChange={(e) => setForm({ ...form, external_url: e.target.value })}
            className="input-base"
            placeholder="https://…"
            data-testid="admin-step-external-input"
          />
        </Field>
      )}

      {(ct === "text" || ct === "video" || ct === "doc" || ct === "link") && (
        <Field label="Notes / Body" hint="Shown below the main content. Markdown-style plain text.">
          <textarea
            value={form.content_md}
            onChange={(e) => setForm({ ...form, content_md: e.target.value })}
            rows={5}
            className="input-base"
            placeholder="Key takeaways, action items, scripts…"
            data-testid="admin-step-content-input"
          />
        </Field>
      )}
    </ModalShell>
  );
}
